import { useCallback, useEffect, useRef, useState } from "react";

import { SLOT_SYMBOLS, type SlotSymbol } from "@koshkodrop/shared";

interface Props {
  reels: [string, string, string] | null;
  spinning: boolean;
  spinKey: number;
  onDone: () => void;
}

const TILE = 76;
const LAND_INDEX = 22;
const STRIP_LENGTH = 28;
/** Барабаны останавливаются по очереди слева направо. */
const REEL_DURATIONS_MS = [1900, 2350, 2800];

export function SlotMachine({ reels, spinning, spinKey, onDone }: Props) {
  const [stoppedCount, setStoppedCount] = useState(0);
  const onDoneRef = useRef(onDone);
  const stoppedRef = useRef(0);

  onDoneRef.current = onDone;

  useEffect(() => {
    if (!spinning) {
      stoppedRef.current = 0;
      setStoppedCount(0);
      return;
    }
    stoppedRef.current = 0;
    setStoppedCount(0);
  }, [spinning, spinKey]);

  const handleReelStop = useCallback(() => {
    stoppedRef.current += 1;
    setStoppedCount(stoppedRef.current);
    if (stoppedRef.current >= 3) {
      onDoneRef.current();
    }
  }, []);

  if (!reels) {
    return (
      <div className="rounded-[28px] border border-white/10 bg-gradient-to-b from-night-700 to-night-950 p-3">
        <div className="grid h-[228px] grid-cols-3 gap-2">
          {[0, 1, 2].map((reel) => (
            <div
              key={reel}
              className="flex items-center justify-center rounded-2xl bg-night-900 text-4xl text-white/20"
            >
              ?
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-b from-night-700 to-night-950 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      <div className="pointer-events-none absolute inset-x-4 top-1/2 z-20 h-[76px] -translate-y-1/2 rounded-2xl border-2 border-paws-400/75 shadow-[0_0_24px_rgba(245,166,35,0.18)]" />

      <div className="pointer-events-none absolute inset-x-0 top-3 z-10 h-10 bg-gradient-to-b from-night-950/95 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 h-10 bg-gradient-to-t from-night-950/95 to-transparent" />

      <div className="grid grid-cols-3 gap-2">
        {reels.map((winnerId, reelIndex) => (
          <ReelColumn
            key={reelIndex}
            reelIndex={reelIndex}
            winnerId={winnerId}
            spinning={spinning}
            spinKey={spinKey}
            onStop={handleReelStop}
          />
        ))}
      </div>

      {spinning && stoppedCount === 3 && (
        <div className="pointer-events-none absolute inset-0 animate-pulse-ring rounded-[28px]" />
      )}
    </div>
  );
}

function ReelColumn({
  reelIndex,
  winnerId,
  spinning,
  spinKey,
  onStop,
}: {
  reelIndex: number;
  winnerId: string;
  spinning: boolean;
  spinKey: number;
  onStop: () => void;
}) {
  const [motion, setMotion] = useState({ offset: 0, animate: false });
  const [landed, setLanded] = useState(false);
  const stripRef = useRef<SlotSymbol[]>(buildStrip(winnerId));

  const symbol = SLOT_SYMBOLS.find((entry) => entry.id === winnerId) ?? SLOT_SYMBOLS[0]!;
  const duration = REEL_DURATIONS_MS[reelIndex] ?? REEL_DURATIONS_MS[2]!;

  useEffect(() => {
    stripRef.current = buildStrip(winnerId);
    const centerOffset = (LAND_INDEX - 1) * TILE;
    setMotion({ offset: centerOffset, animate: false });
    setLanded(false);
  }, [winnerId]);

  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;

  useEffect(() => {
    if (!spinning) return;

    stripRef.current = buildStrip(winnerId);
    setLanded(false);
    setMotion({ offset: 0, animate: false });

    const jitter = (Math.random() - 0.5) * (TILE * 0.42);
    const target = (LAND_INDEX - 1) * TILE + jitter;

    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setMotion({ offset: target, animate: true }));
    });

    const timer = window.setTimeout(() => {
      setLanded(true);
      onStopRef.current();
    }, duration + 40);

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      window.clearTimeout(timer);
    };
  }, [spinning, spinKey, winnerId, duration]);

  if (!spinning) {
    return (
      <div className="relative h-[228px] overflow-hidden rounded-2xl bg-night-900">
        <div className="flex h-full flex-col items-center justify-center text-5xl">
          {symbol.emoji}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[228px] overflow-hidden rounded-2xl bg-night-900">
      <div
        className="flex flex-col will-change-transform"
        style={{
          transform: `translateY(${-motion.offset}px)`,
          transition: motion.animate
            ? `transform ${duration}ms cubic-bezier(0.08, 0.86, 0.12, 1)`
            : "none",
          filter: motion.animate && !landed ? "blur(1.2px)" : "none",
        }}
      >
        {stripRef.current.map((entry, index) => (
          <div
            key={`${spinKey}-${index}`}
            className="flex h-[76px] items-center justify-center text-4xl"
          >
            {entry.emoji}
          </div>
        ))}
      </div>
    </div>
  );
}

function buildStrip(winnerId: string): SlotSymbol[] {
  const fallback = SLOT_SYMBOLS[0]!;
  const winner = SLOT_SYMBOLS.find((symbol) => symbol.id === winnerId) ?? fallback;
  const strip: SlotSymbol[] = [];

  for (let index = 0; index < STRIP_LENGTH; index += 1) {
    if (index === LAND_INDEX) {
      strip.push(winner);
      continue;
    }
    strip.push(SLOT_SYMBOLS[Math.floor(Math.random() * SLOT_SYMBOLS.length)] ?? fallback);
  }

  return strip;
}
